import React, { useRef, type MouseEvent as ReactMouseEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Copy, Trash2 } from '../emailBuilderIcons';
import { EMAIL_BUILDER_ACTION_RAIL_ATTR } from './actionRailConstants';
import {
  emailBuilderCanvasBlockRailActionTooltip,
  emailBuilderCanvasBlockReorderRailTooltip,
} from '../editing/emailBuilderCanvasBlockInteractions';
import { deleteSelectionWithConfirm } from '../lib/deleteSelection';
import { getMultiSelectBlockCapabilities } from '../domain/selection/block-bulk-ops';
import { selectDocument, selectSelectedBlockIds, selectSelection } from '../domain/selectors';
import { useEditorActions, useEditorStore } from '../domain/store';
import { useConfirm } from '../shell/EmailBuilderConfirmProvider';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useStableScopedDomId } from '../useStableDomId';
import { useEmailBuilderVerticalActionRailPin } from './useEmailBuilderVerticalActionRailPin';
import {
  emailBuilderVerticalActionRailClassName,
  emailBuilderVerticalActionRailPinStyle,
} from './emailBuilderVerticalActionRailChrome';

function isMacPlatform(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)
  );
}

type MultiSelectRailButtonProps = {
  action: string;
  tooltip: string;
  ariaLabel: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

function MultiSelectRailButton({
  action,
  tooltip,
  ariaLabel,
  disabled = false,
  danger = false,
  onClick,
  children,
}: MultiSelectRailButtonProps) {
  const tooltipId = useStableScopedDomId('email-builder-multi-select-rail', action);

  const button = (
    <button
      type="button"
      className={"email-builder-canvas-rail-btn"}
      data-danger={danger ? 'true' : undefined}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}>
      {children}
    </button>
  );

  return (
    <OverlayTrigger
      placement="right"
      flip
      delay={{ show: 400, hide: 100 }}
      overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
      {disabled ? <span className={"email-builder-canvas-rail-tooltip-target"}>{button}</span> : button}
    </OverlayTrigger>
  );
}

type MultiSelectActionRailProps = {
  layout?: 'vertical' | 'inline';
  frameRef?: RefObject<HTMLElement | null>;
  onClaimHover?: (event: ReactMouseEvent<HTMLElement>) => void;
};

export function MultiSelectActionRail({
  layout = 'vertical',
  frameRef,
  onClaimHover,
}: MultiSelectActionRailProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const selection = useEditorStore(selectSelection);
  const selectedBlockIds = useEditorStore(selectSelectedBlockIds);
  const { duplicateSelected, deleteSelected, deleteGroup, moveSelected } =
    useEditorActions();
  const { confirm } = useConfirm();
  const railRef = useRef<HTMLDivElement>(null);
  const portaled = layout === 'vertical' && frameRef !== undefined;
  const pinStyle = useEmailBuilderVerticalActionRailPin({
    enabled: portaled,
    frameRef,
    railRef,
  });

  const capabilities = getMultiSelectBlockCapabilities(document, selectedBlockIds);
  const showMoveButtons = capabilities?.showMoveButtons ?? false;

  if (!capabilities || selectedBlockIds.length <= 1) {
    return null;
  }

  const { canUp, canDown, canDuplicate, canDelete, upLabel, downLabel } =
    capabilities;
  const isMac = isMacPlatform();
  const duplicateTooltip = emailBuilderCanvasBlockRailActionTooltip(
    'duplicate',
    t('Duplicate'),
    isMac,
  );
  const deleteTooltip = emailBuilderCanvasBlockRailActionTooltip(
    'delete',
    t('Delete'),
    isMac,
  );

  function handleDelete() {
    void deleteSelectionWithConfirm(
      document,
      selection,
      { confirm },
      { deleteSelected, deleteGroup },
      t,
    );
  }

  const railClassName =
    layout === 'inline'
      ? "email-builder-canvas-rail-inline-group"
      : emailBuilderVerticalActionRailClassName(portaled);

  const verticalRailStyle =
    layout === 'inline' ? undefined : emailBuilderVerticalActionRailPinStyle(portaled, pinStyle);

  return (
    <div
      ref={railRef}
      className={railClassName}
      data-editor-chrome
      role="toolbar"
      aria-label={t('Selected blocks')}
      {...{ [EMAIL_BUILDER_ACTION_RAIL_ATTR]: '' }}
      style={verticalRailStyle}
      onMouseOver={onClaimHover}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}>
      {showMoveButtons ? (
        <>
          <MultiSelectRailButton
            action="move-back"
            tooltip={emailBuilderCanvasBlockReorderRailTooltip('move-earlier', t(upLabel), isMac)}
            ariaLabel={emailBuilderCanvasBlockReorderRailTooltip('move-earlier', t(upLabel), isMac)}
            disabled={!canUp}
            onClick={() => moveSelected(-1)}>
            <ChevronUp size={16} aria-hidden />
          </MultiSelectRailButton>
          <MultiSelectRailButton
            action="move-forward"
            tooltip={emailBuilderCanvasBlockReorderRailTooltip('move-later', t(downLabel), isMac)}
            ariaLabel={emailBuilderCanvasBlockReorderRailTooltip('move-later', t(downLabel), isMac)}
            disabled={!canDown}
            onClick={() => moveSelected(1)}>
            <ChevronDown size={16} aria-hidden />
          </MultiSelectRailButton>
        </>
      ) : null}
      <MultiSelectRailButton
        action="duplicate"
        tooltip={duplicateTooltip}
        ariaLabel={duplicateTooltip}
        disabled={!canDuplicate}
        onClick={duplicateSelected}>
        <Copy size={16} aria-hidden />
      </MultiSelectRailButton>
      <MultiSelectRailButton
        action="delete"
        tooltip={deleteTooltip}
        ariaLabel={deleteTooltip}
        danger
        disabled={!canDelete}
        onClick={handleDelete}>
        <Trash2 size={16} aria-hidden />
      </MultiSelectRailButton>
    </div>
  );
}
